import * as utils from './Utils';

export function resolveFirst(selectors, root=document, requireVisible=false) {
  for (const sel of selectors) {
    const el = root.querySelector(sel);
    if (!el) continue;
    if (requireVisible && el.offsetParent === null) continue;
    return el;
  }
  return null;
}

export function getBigCommerceBisPdpAnchors() {
  // Anchor BIS auto placement on the add-to-cart <form>, `[data-cart-item-add]`. It is
  // present AND visible in BOTH in-stock and out-of-stock states — BC only toggles the
  // inner #add-to-cart-wrapper / #form-action-addToCart — so the widget lands in the same
  // spot regardless of stock. Stock-state-dependent nodes are deliberately NOT anchors:
  //   - #form-action-addToCart is display:none on OOS (the exact case BIS renders in)
  //   - .alertBox.productAttributes-message only appears in some states
  // Using them would move the widget depending on stock. `.productView-options` is a
  // stable wrapper fallback for themes without data-cart-item-add.
  return [
    '[data-cart-item-add]',
    '.productView-options',
  ];
}

export function getBigCommerceReviewsWidgetPdpAnchor() {
  // Reviews widget lands at the BOTTOM of the product view — after the description/tabs,
  // right before the related / "recommended products" block. Resolved in tiers because BC
  // themes vary wildly:
  //
  //   1. The related-products block, matched by the "related" keyword on id OR class
  //      (#tab-related, #related_products, #halo-related-products, .related-products-container,
  //      .tabs-contents related, ...). These are all recommended-products sections; the
  //      widget inserts BEFORE the first one in document order. requireVisible skips hidden
  //      inactive tab panels.
  //   2. The Stencil "below product" region — product_below_content, or product_below_tabs
  //      on themes that lack it (page4). Inserted BEFORE the region.
  //   3. Last resort: after the whole .productView (only if a theme has neither — none of
  //      the sampled storefronts hit this).
  //
  // Returns { node, position } or null.
  const related = resolveFirst([
    '[id*="related" i], [class*="related" i]',
  ], document, true);
  if (related) return { node: related, position: 'before' };

  const region = resolveFirst([
    '[data-content-region="product_below_content"]',
    '[data-content-region="product_below_tabs"]',
  ], document, true);
  if (region) return { node: region, position: 'before' };

  const productView = resolveFirst(['.productView'], document, true);
  if (productView) return { node: productView, position: 'after' };

  return null;
}

export function getBigCommerceAovPdpAnchors() {
  // AOV progress bar inserts just BEFORE the add-to-cart form on the product page — a
  // spend-more / free-shipping nudge reads best right above the buy button. Same
  // stock-stable [data-cart-item-add] anchor BIS uses, with wrapper fallbacks so the bar
  // still lands in the buy-box area on themes that lack the data attribute.
  return [
    '[data-cart-item-add]',
    '.productView-options',
    '.productView-details',
  ];
}

export function getBigCommerceCartAnchors() {
  // AOV bar on the cart page goes at the TOP of the cart content — inserted BEFORE
  // [data-cart-content]. That element is the region BC re-renders via AJAX on qty change /
  // item removal, so we anchor as its SIBLING (above it), never inside it — otherwise the
  // widget is wiped on every cart update. Present across themes (Cornerstone + custom, where
  // it's e.g. .cart-contentWrapper[data-cart-content]). Fallbacks keep it above the line
  // items on themes without the attribute; the header mini-cart (#cart-preview-dropdown,
  // .mobile-cart) is deliberately NOT matched.
  return [
    '[data-cart-content]',
    'table.cart',
    '[class*="cart-content" i]',
    '[class*="cart-page" i]',
  ];
}

export function getBigCommercePopupContainer() {
  // BC's add-to-cart preview modal (#previewModal) is injected by the Stencil theme AFTER a
  // shopper adds to cart — it is NOT in the DOM at page load, so a boot-time query finds
  // nothing. `.previewCart` is the stable inner container across every sampled theme (default
  // preview modal + fastCart variants); the surrounding markup (.previewCartCheckout /
  // .productView / .modal-header) varies. Only return it when the modal is actually open so we
  // don't inject into a hidden/stale shell. Returns the .previewCart node or null.
  const modal = document.querySelector('#previewModal.open, #previewModal[aria-hidden="false"]');
  if (!modal) return null;
  return modal.querySelector('.previewCart') || null;
}

export function setupBigCommercePopupPlacement(el, options) {
  // Add-to-cart popup placement. The widget is rendered ONCE into `el`; React content persists
  // on the node even while it's detached, so we hold `el` off-DOM and move the SAME node into
  // the preview modal's .previewCart each time the modal appears. BC rebuilds .previewCart on
  // every subsequent add (a fresh container node), detaching our node — a persistent body
  // observer catches that and re-injects.
  //
  // options.position:
  //   'top' (default) — insert as the first child of .previewCart, above the cart summary. A
  //     free-shipping/progress nudge (AOV) reads best there.
  //   'bottom' — append as the LAST child of .modal-content, below the whole .modal-body
  //     (which holds .previewCart: the checkout + product cart preview). .previewCart is the
  //     cart content itself and is a side-by-side flex row, so a recommendation carousel must
  //     NOT go inside it — it belongs under everything, at the very bottom of the modal ("the
  //     last just before close"). .modal-content is also the most stable anchor: BC's fastCart
  //     re-renders .previewCart / .modal-body internals on each add, but not .modal-content, so
  //     a node parked here survives without needing constant re-injection.
  // options.onInject (optional) fires only when the node is moved into a NEW modal instance
  //   (once per open, not on every mutation) — reserved for a future recommender live-cart
  //   refresh; AOV needs none (its cart-update event keeps it fresh).
  const opts = options || {};
  const position = opts.position || 'top';
  const onInject = opts.onInject;

  if (el && el.parentNode) el.parentNode.removeChild(el);

  let lastContainer = null;
  const inject = () => {
    const previewCart = getBigCommercePopupContainer();
    if (!previewCart || !el) return;

    // bottom -> the whole modal's .modal-content (append below .modal-body); top -> inside
    // .previewCart as the first child.
    const host = position === 'bottom'
      ? (previewCart.closest('.modal-content') || previewCart.parentNode || previewCart)
      : previewCart;
    if (el.parentNode === host) return; // already placed in this modal instance

    if (position === 'bottom') {
      host.appendChild(el);
    } else {
      host.insertBefore(el, host.firstChild);
    }

    if (onInject && previewCart !== lastContainer) {
      lastContainer = previewCart;
      try { onInject(); } catch (error) { console.error(error); }
    }
  };

  inject();

  const observer = new MutationObserver(() => inject());
  observer.observe(document.body, { childList: true, subtree: true });
}

export function getBigCommerceCheckoutAnchor() {
  // Optimized One-Page Checkout is a separate hosted React app (checkout-js), not the
  // merchant's Stencil theme, so none of the Stencil anchors above exist here. Its content
  // column is `.layout-main` (sibling of the `.layout-cart` order-summary aside, both inside
  // `.layout`), holding the express-checkout buttons then `<ol class="checkout-steps">`.
  //
  // The bar goes in as the FIRST CHILD of .layout-main — the top of the content column, above
  // "Check out faster with:" — so it reads directly under the store logo without being inside
  // the header, which is its own centred flex block and would fight a full-width bar.
  return document.querySelector('.layout-main');
}

export function setupBigCommerceCheckoutPlacement(el) {
  // Checkout placement. Same off-DOM-hold-and-move approach as setupBigCommercePopupPlacement:
  // the node is rendered once and re-attached wherever the anchor currently lives. checkout-js
  // is a client-rendered SPA that swaps its step views as the shopper advances (customer ->
  // shipping -> billing -> payment) and can remount the column wholesale when returning from a
  // hosted payment step, so a persistent observer re-injects rather than a one-shot insert.
  //
  // Safe no-op when the anchor is absent. The marker div is emitted only under the
  // page_type=='checkout' gate in worki's appscripts template, so this should not run
  // elsewhere at all — order confirmation (page_type=='orderconfirmation') never gets a bar,
  // which matters because the cart is empty by then and a "spend more" nudge post-purchase
  // would be wrong.
  if (el && el.parentNode) el.parentNode.removeChild(el);

  const inject = () => {
    const main = getBigCommerceCheckoutAnchor();
    if (!main || !el) return;
    if (el.parentNode === main && main.firstChild === el) return; // already first child

    main.insertBefore(el, main.firstChild);
  };

  inject();

  const observer = new MutationObserver(() => inject());
  observer.observe(document.body, { childList: true, subtree: true });
}

export function getBigCommercePdpPriceAnchors() {
  return [
    '[data-content-region="product_below_price"]',
    '.productView-rating',
    '.productView-price',
    '.price-section--main',
  ];
}

export function getBigCommerceProductCards() {
  // EVERY product card on the page, across every context — home carousels, category grid,
  // search results, product-recommendation carousels, brand pages. Cornerstone wraps each
  // card in <li class="product"> (covers home/category/featured); themes that don't are
  // caught by any list item / card article carrying a product id. Skeleton/sample cards
  // (.product-sample, no id) and non-card rows (e.g. the bulk-order header) are filtered
  // downstream, where each card must yield BOTH a product id AND a price element.
  const cards = new Set();
  document.querySelectorAll('li.product').forEach((li) => cards.add(li));
  document
    .querySelectorAll('article.card[data-entity-id], li[data-product-id], li[data-entity-id]')
    .forEach((el) => cards.add(el.closest('li') || el));
  return Array.from(cards);
}

export function getBigCommerceGridItems() {
  // Page-wide card list + a `root` for the mutation observer that re-scans when carousels
  // hydrate late. root is the main content wrapper so the observer covers every grid.
  const items = getBigCommerceProductCards();
  const root = items.length ? (document.querySelector('main.body, .body') || document.body) : null;
  return { root, items };
}

export function resolveGridItemProductId(li) {
  return li.dataset.productId
    || li.querySelector('[data-product-id]')?.dataset.productId
    || li.querySelector('[data-entity-id]')?.dataset.entityId
    || null;
}

export function getBigCommerceCardPriceEl(li) {
  return li.querySelector('[data-test-info-type="price"]')
    || li.querySelector('.card-text--price')
    || li.querySelector('.card-title');
}

export function insertAfter(newNode, ref) {
  ref.parentNode.insertBefore(newNode, ref.nextSibling);
}

export function loadStorefrontProductData(callback) {
  if (!window.OnVoardData || !window.OnVoardData.product || !window.OnVoardData.storefront_api_token) return;

  if (!window.OnVoardData.storefront) window.OnVoardData.storefront = {};
  if (window.OnVoardData.storefront.product) {
    callback(window.OnVoardData.storefront.product);
    return;
  }

  let apiToken = window.OnVoardData.storefront_api_token;
  let variables = {};
  variables['productId'] = parseInt(window.OnVoardData.product.id);

  let query = `
    query productById($productId: Int!) {
      site {
        product(entityId: $productId) {
          entityId
          name
          sku
          upc
          type
          availabilityV2 {
            status
          }
          inventory {
            isInStock
          }
          brand {
            name
          }
          defaultImage {
            urlOriginal
          }
          prices {
            ...PricesFields
          }
          variants(first: 250) {
            edges {
              node {
                ...VariantFields
              }
            }
          }
          categories(first: 50) {
            edges {
              node {
                name
              }
            }
          }
        }
      }
    }

    fragment VariantFields on Variant {
      entityId
      sku
      upc
      defaultImage {
        urlOriginal
      }
      prices {
        ...PricesFields
      }
      inventory {
        isInStock
      }
      options(first: 50) {
        edges {
          node {
            ...ProductOptionFields
          }
        }
      }
    }

    fragment ProductOptionFields on ProductOption {
      entityId
      displayName
      isRequired
      values(first: 50) {
        edges {
          node {
            entityId
            label
          }
        }
      }
    }

    fragment PricesFields on Prices {
      price {
        ...MoneyFields
      }
      basePrice {
        ...MoneyFields
      }
      salePrice {
        ...MoneyFields
      }
      retailPrice {
        ...MoneyFields
      }
      saved {
        ...MoneyFields
      }
      priceRange {
        min {
          ...MoneyFields
        }
        max {
          ...MoneyFields
        }
      }
    }

    fragment MoneyFields on Money {
      value
      currencyCode
    }
  `;

  fetch('/graphql', {
    method: 'POST',
    credentials: 'same-origin',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${apiToken}`
    },
    body: JSON.stringify({
      query: query,
      variables: variables,
    }),
  })
  .then(res => res.json())
  .then(json => {
    let result = utils.cleanGraphQLResponse(json);
    window.OnVoardData.storefront.product = result.data.site.product;
    callback(window.OnVoardData.storefront.product);
  });
}

export function convertDefaultRate(amount, amountCur='') {
  if (amountCur && amountCur === window.OnVoardData.currency_selector.default_currency_code) return amount;
  let exchange_rate = window.OnVoardData.exchange_rate;
  if (!exchange_rate) return amount;

  amount = parseFloat(amount)/parseFloat(exchange_rate);
  amount = Math.round(amount * 100) / 100; // round to 2 decimal float
  amount = Math.round(amount * 20) / 20; // round off to nearest 5 cents
  return amount;
}

export function convertActiveRate(amount, amountCur='') {
  if (amountCur && amountCur === window.OnVoardData.currency_selector.active_currency_code) return amount;
  let exchange_rate = window.OnVoardData.exchange_rate;
  if (!exchange_rate) return amount;

  amount = parseFloat(amount)*parseFloat(exchange_rate);
  amount = Math.round(amount * 100) / 100; // round to 2 decimal float
  return amount;
}

export function executeWithRates(callback) {
  let currency = window.OnVoardData.currency_selector;
  if (currency.default_currency_code === currency.active_currency_code || window.OnVoardData.exchange_rate) {
    callback();
    return;
  }

  // diff rates, need to load rates first
  fetch('/graphql', {
    method: 'POST',
    credentials: 'same-origin',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${window.OnVoardData.storefront_api_token}`
    },
    body: JSON.stringify({
      query: `
        query getCurrencyDetails($currencyCode: currencyCode!) {
          site {
            currency(currencyCode: $currencyCode) {
              entityId
              code
              name
              isActive
              exchangeRate
              isTransactional
              display {
                decimalToken
                thousandsToken
                decimalPlaces
              }
            }
          }
        }
      `,
      variables: {
        'currencyCode': currency.active_currency_code
      },
    }),
  })
  .then(res => res.json())
  .then(json => {
    window.OnVoardData['exchange_rate'] = json.data.site.currency.exchangeRate;
    callback();
  });
}

export function extractProductCurrency(product) {
  let withoutTax = product.price.without_tax;
  let withTax = product.price.with_tax;
  if (withoutTax && withoutTax.currency) return withoutTax.currency;
  if (withTax && withTax.currency) return withTax.currency;
  return '';
}

export function extractProductPrice(product) {
  let withoutTax = product.price.without_tax;
  let withTax = product.price.with_tax;
  if (withoutTax) return withoutTax.value;
  if (withTax) return withTax.value;
  return 0;
}

export function extractProductCompareAtPrice(product) {
  let withoutTax = product.price.non_sale_price_without_tax;
  let withTax = product.price.non_sale_price_with_tax;
  if (withoutTax) return withoutTax.value;
  if (withTax) return withTax.value;
  return 0;
}
